import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import { getProducts } from '../../../api/product/queries/getProducts';
import { Button } from '../../../components/common/buttons/Button';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { ToastViewer } from '../../../components/common/text-viewer/ToastViewer';
import { newProductPageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetNewProductByIdQuery } from '../../../hooks/api/product/queries/useGetNewProductById.query';
import { useGetNextOpenMarketProductsQuery } from '../../../hooks/api/product/queries/useGetNextOpenMarketProductsQuery';
import { useGetNewProductsQuery } from '../../../hooks/api/useGetNewProducts.query';
import { useGetBannersQuery } from '../../../hooks/banners/queries/useGetBanners.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useCheckUserLoggedIn } from '../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../hooks/user/useGetUserProfile';
import { IProduct } from '../../../interfaces/Product.interfaces';
import { formatPrice } from '../../../utils/formatter/formatPrice.util';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';
import { OpenMarketQuadSwiperGroup } from '../../../views/Product/detailView/components/OpenMarketQuadSwiperGroup';
import { ProductImageSection } from '../../../views/Product/detailView/components/ProductImageSection';
import { ProductQuadSwiperGroup } from '../../../views/Product/detailView/components/ProductQuadSwiperGroup';
import { ProductYoutubeSection } from '../../../views/Product/detailView/ProductDetailView/components/ProductYoutubeSection';
import { SecitionDivideLine } from '../../../views/Product/detailView/ProductDetailView/components/SecitionDivideLine';
import { MainCarousel } from '../../Home/HomePage/components/MainCarousel';
import { InstallmentPossibleSection } from '../../Product/ProductDetailPage/components/InstallmentPossibleSection';
import { ProductDetailPageFooter } from '../../Product/ProductDetailPage/components/ProductDetailPageFooter';

const MAIN_HEAD_HEIGHT = 48;
const INSTALLMENT_POSSIBLE_SECTION_HEIGHT = 36.8;

export const NewProductDetailPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { userProfile } = useGetUserProfile();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const { data: product, isLoading: isGetProductQueryLoading } = useGetNewProductByIdQuery({
    productId: productId!,
  });

  const { data: newProducts, isLoading: isGetNewProductsQueryLoading } = useGetNewProductsQuery({
    options: {
      select: (data) => {
        return data.filter((product) => product.status !== 'PENDING');
      },
    },
  });

  const productIndexOnList = useMemo<number>(() => {
    return newProducts?.findIndex((product) => product.id === productId) ?? -1;
  }, [newProducts, productId]);

  const { data: openMarketProducts, isLoading: isGetOpenMarketProductsQueryLoading } =
    useGetNextOpenMarketProductsQuery({
      searchParam: {
        id: productId!,
      },
    });

  const { data: pluvaProducts } = useQuery<IProduct[], AxiosError>(
    ['products', 'category=PLUVA', 'status=ONSALE'],
    () =>
      getProducts({
        searchParam: {
          category: 'PLUVA',
          status: 'ONSALE',
          count: 8,
        },
      }),
  );

  const { data: mainCarouselImages } = useGetBannersQuery({
    searchParam: {
      category: 'CAROUSEL_MAIN',
      // TODO: 추후 outputLocation에 맞는 내용 넣을 수 있도록 리팩토링 필요함.https://gint-team.atlassian.net/browse/PVM-2280
      outputLocation: 'home',
    },
  });

  const isAdmin = useMemo<boolean>(() => {
    return !!userProfile?.isAdmin;
  }, [userProfile]);

  const handleProductInquiryButtonClick = () => {
    if (!product) return;

    const clickEventProperties = {
      id: product.id,
      title: product.title,
      price: product.price,
      installment_yn: product.installmentYn,
      negotiation_yn: product.negotiationYn,
      section: 'new-product-detail-fab',
    };

    ampli.pvmClickNewProductInquiry(clickEventProperties);
    ReactGA.event('pvm_click_new_product_inquiry', {
      clickEventProperties,
    });

    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: 'tel:0316074997' });
    } else {
      window.location.href = 'tel:0316074997';
    }
  };

  useLayout({
    fixedHeader: (
      <>
        <MainHead
          backButton={{ isShow: true, directlyPassedURL: newProductPageInfo.listPage.path }}
          headerTitle={isAdmin && product ? product.code : ''}
        />
        {product && product.installmentYn && (
          <InstallmentPossibleSection
            color={'#34C77E'}
            backgroundColor={'#D6F4E5'}
            boldText={'할부가 가능'}
            text={'해요!'}
          />
        )}
      </>
    ),
    fixedFooter: (
      <ProductDetailPageFooter>
        <ButtonWrapper>
          <Button
            borderType={'square'}
            size={'lg'}
            color={'filledPrimaryStrong'}
            text={{ size: 'lg', content: '문의 하기 031-607-4997' }}
            onClick={() => {
              handleProductInquiryButtonClick();
            }}
          />
        </ButtonWrapper>
      </ProductDetailPageFooter>
    ),
    fixedHeaderHeight:
      product && product.installmentYn ? MAIN_HEAD_HEIGHT + INSTALLMENT_POSSIBLE_SECTION_HEIGHT : MAIN_HEAD_HEIGHT,
    fixedFooterHeight: 0,
  });

  useEffect(() => {
    if (product) {
      if (product.status !== 'ONSALE') {
        navigate('/');
      }
    }
  }, [product, navigate]);

  if (isGetProductQueryLoading) return <AnimateLoading />;

  if (product) {
    return (
      <>
        <Helmet>
          <title>{`${product.title} - Pluva Market`}</title>
        </Helmet>

        <ProductImageSection
          images={product.images.filter((image) => image.type === 'DETAIL')}
          status={product.status}
          pageType={'new-product'}
          productTitle={product.title}
          productCode={product.code}
          onProductImageClick={() => {
            ampli.pvmClickNewProductImage({
              id: product.id,
              index: productIndexOnList.toLocaleString(),
            });
          }}
        />

        <ProductTitleSection>
          <ProductTitle>{product.title}</ProductTitle>
          {isUserLoggedIn && !product.negotiationYn && <ProductPrice>{`${formatPrice(product.price)}원`}</ProductPrice>}
          {isUserLoggedIn && product.negotiationYn && <ProductPrice>{'가격 문의'}</ProductPrice>}
          {!isUserLoggedIn && (
            <ProductPriceWrapper>
              <PriceScreenSection />
              <ProductPrice>{`${formatPrice(product.price)}원`}</ProductPrice>
            </ProductPriceWrapper>
          )}
        </ProductTitleSection>

        <SecitionDivideLine />

        {product.content1 && (
          <ContentWrapper>
            <ContentBackground>
              <ToastViewer content={product.content1} />
            </ContentBackground>
          </ContentWrapper>
        )}

        {product.youtubeUrl && (
          <ProductYoutubeSection
            product={product as unknown as IProduct}
            pageType={'new-product'}
            productIndexOnList={productIndexOnList}
            title={'이 상품을 영상으로 만나보세요'}
          />
        )}

        {product.content2 && (
          <ContentWrapper>
            <ContentBackground>
              <ToastViewer content={product.content2} />
            </ContentBackground>
          </ContentWrapper>
        )}

        <ProductInfoContainer>
          {pluvaProducts && pluvaProducts.length >= 4 && (
            <Section>
              <ProductQuadSwiperGroup
                title={'보증을 제공하는 인증 트랙터 더보기'}
                products={pluvaProducts}
                goToButton={{ renderedIn: 'ndp', itemType: 'pluva' }}
                pageType={'ndp'}
              />
            </Section>
          )}
          {openMarketProducts && openMarketProducts.length >= 4 && (
            <Section>
              <OpenMarketQuadSwiperGroup
                pageType={'ndp'}
                title={'다양한 회원 직거래 매물 더보기'}
                openMarketProducts={openMarketProducts}
                goToButton={{ renderedIn: 'ndp', itemType: 'open-market' }}
              />
            </Section>
          )}

          {mainCarouselImages && mainCarouselImages.length > 0 && (
            <MainCarousel carouselImages={mainCarouselImages} pageType={'pdp'} position={'bottom'} />
          )}
        </ProductInfoContainer>
      </>
    );
  }

  return null;
};

const PriceScreenSection = () => {
  return (
    <Container>
      <TitleSection>
        <RedTitle>{'로그인하고 매물 가격을 확인하세요'}</RedTitle>
      </TitleSection>
    </Container>
  );
};

const ProductTitleSection = styled.div`
  position: relative;

  display: flex;
  padding: 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ProductTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const ProductPriceWrapper = styled.div`
  display: flex;
  padding: 40px 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  position: relative;
`;

const ProductPrice = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const Container = styled.div`
  display: flex;
  width: calc(100% + 32px);
  height: calc(100% + 14px);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;

  position: absolute;
  left: -16px;
  top: -8px;

  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(4px);
`;

const TitleSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const RedTitle = styled(Title)`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);
`;

const ContentWrapper = styled.div`
  display: flex;
  padding: 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const ContentBackground = styled.div``;

const ProductInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Section = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  padding: 32px var(--radius-large, 16px);
  gap: 24px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;

  position: relative;
`;
